import React, { useCallback, useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import CreatorRichEditor from './CreatorRichEditor';
import { ExecuteHeaderBar } from './PromptExecuteChatPanel';

const creatorMobilePaneTabsSx = {
  flexShrink: 0,
  minHeight: 40,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontSize: '0.8rem' },
};

export default function CreatorExecuteWorkspace({
  isMobile = false,
  headerProps,
  creatorDocument = '',
  onCreatorDocumentChange,
  chatPanel,
  storageKey = 'creatorChatWidthPx',
  defaultChatWidth = 380,
  minChatWidth = 320,
  maxChatWidthRatio = 0.55,
}) {
  const [chatWidthPx, setChatWidthPx] = useState(defaultChatWidth);
  const [isResizingChat, setIsResizingChat] = useState(false);
  const [mobilePane, setMobilePane] = useState('document');

  useEffect(() => {
    if (isMobile) return;
    const raw = localStorage.getItem(storageKey);
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return;
    const max = Math.min(Math.floor(window.innerWidth * maxChatWidthRatio), 760);
    setChatWidthPx(Math.min(max, Math.max(minChatWidth, parsed)));
  }, [isMobile, maxChatWidthRatio, minChatWidth, storageKey]);

  useEffect(() => {
    if (isMobile) return;
    localStorage.setItem(storageKey, String(chatWidthPx));
  }, [chatWidthPx, isMobile, storageKey]);

  const startChatResize = useCallback((e) => {
    if (isMobile) return;
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = chatWidthPx;
    const max = Math.min(Math.floor(window.innerWidth * maxChatWidthRatio), 760);
    setIsResizingChat(true);

    const onMove = (moveEvent) => {
      const delta = startX - moveEvent.clientX;
      const nextWidth = startWidth + delta;
      setChatWidthPx(Math.min(max, Math.max(minChatWidth, nextWidth)));
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setIsResizingChat(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [chatWidthPx, isMobile, maxChatWidthRatio, minChatWidth]);

  if (isMobile) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: '#ffffff',
        }}
      >
        {headerProps ? <ExecuteHeaderBar {...headerProps} chromePlacement="full" /> : null}
        <Tabs
          value={mobilePane}
          onChange={(_, next) => setMobilePane(next)}
          variant="fullWidth"
          sx={creatorMobilePaneTabsSx}
        >
          <Tab label="Document" value="document" />
          <Tab label="Chat" value="chat" />
        </Tabs>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: mobilePane === 'document' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            <CreatorRichEditor
              valueMarkdown={creatorDocument}
              onChangeMarkdown={onCreatorDocumentChange}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: mobilePane === 'chat' ? 'flex' : 'none',
              flexDirection: 'column',
            }}
          >
            {chatPanel}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#ffffff' }}>
          <CreatorRichEditor
            valueMarkdown={creatorDocument}
            onChangeMarkdown={onCreatorDocumentChange}
            hideRightDivider
          />
        </Box>
      </Box>
      <Box
        onMouseDown={startChatResize}
        sx={{
          width: 6,
          flexShrink: 0,
          cursor: 'col-resize',
          display: 'flex',
          position: 'relative',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          bgcolor: 'transparent',
          '&::before': {
            content: '""',
            width: '1px',
            height: '100%',
            bgcolor: isResizingChat ? 'primary.main' : 'divider',
            opacity: isResizingChat ? 1 : 0.85,
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
          },
        }}
      />
      <Box
        sx={{
          width: chatWidthPx,
          minWidth: minChatWidth,
          maxWidth: `${maxChatWidthRatio * 100}%`,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {headerProps ? <ExecuteHeaderBar {...headerProps} chromePlacement="chatColumn" /> : null}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {chatPanel}
        </Box>
      </Box>
    </Box>
  );
}
